class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestId = 0;
    this.useSearchFallback = false;
    try {
      const features = JSON.parse(document.getElementById('shopify-features')?.textContent || '{}');
      this.useSearchFallback ||= features.predictiveSearch === false;
    } catch (_) {
      // A missing feature flag must not prevent search from working.
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (!previousTerm || currentButtonText.split(previousTerm).length > 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const requestId = ++this.requestId;
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      let section = this.useSearchFallback ? 'search-suggestions' : 'predictive-search';
      const request = () => {
        const params = new URLSearchParams({ q: searchTerm, section_id: section });
        return fetch(`${routes.predictive_search_url}?${params}`, { signal });
      };
      let markup;
      if (!this.useSearchFallback) {
        const response = await request();
        if (response.status === 417) {
          this.useSearchFallback = true;
          section = 'search-suggestions';
        } else {
          if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
          markup = new DOMParser().parseFromString(await response.text(), 'text/html')
            .querySelector('#shopify-section-predictive-search');
        }
      }
      if (this.useSearchFallback) markup = await this.getFallbackResults(searchTerm, signal);
      if (!markup?.querySelector('[data-predictive-search-live-region-count-value]')) throw new Error('Invalid search response');
      if (signal.aborted || requestId !== this.requestId || this.getQuery() !== searchTerm) return;
      const resultsMarkup = markup.innerHTML;
      this.allPredictiveSearchInstances.forEach((instance) => {
        instance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (signal.aborted || error?.name === 'AbortError' || requestId !== this.requestId) return;
      this.close();
      // The regular search form remains usable if suggestions are temporarily unavailable.
      console.warn('Search suggestions unavailable:', error.message);
    }
  }

  async getFallbackResults(searchTerm, signal) {
    const searchUrl = routes.search_url || this.input.form.getAttribute('action');
    const load = async (type, query) => {
      const params = new URLSearchParams({ q: query, type, section_id: 'search-suggestions',
        'options[prefix]': 'last', 'options[unavailable_products]': 'last', sort_by: 'relevance' });
      const response = await fetch(`${searchUrl}?${params}`, { signal });
      if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
      const markup = new DOMParser().parseFromString(await response.text(), 'text/html')
        .querySelector('#shopify-section-search-suggestions');
      if (!markup?.querySelector('[data-predictive-search-live-region-count-value]')) throw new Error('Invalid search response');
      return markup;
    };
    // Pages have their own small result budget; products cannot push them out.
    const [products, pages] = await Promise.all([
      load('product', searchTerm),
      load('page,article', searchTerm).catch(() => null)
    ]);
    const pageGroup = pages?.querySelector('[data-search-pages]');
    if (pageGroup?.querySelector('li')) {
      pageGroup.hidden = false;
      products.querySelector('[data-search-pages]')?.replaceWith(pageGroup);
    }
    this.prepareFallbackGroups(products, searchTerm);
    return products;
  }

  prepareFallbackGroups(markup, searchTerm) {
    const doc = markup.ownerDocument;
    const normalize = value => value.toLocaleLowerCase().normalize('NFKC').replace(/[\u064B-\u065F\u0670]/g, '').trim();
    const query = normalize(searchTerm);
    const candidates = [];
    const seen = new Set();
    const stopWords = new Set(['de', 'du', 'des', 'with', 'and', 'for', 'the', 'in', 'on', 'to', 'or', 'of']);
    const add = (title, href) => {
      const key = normalize(title);
      if (!key || !key.startsWith(query) || seen.has(key) || candidates.length >= 5) return;
      seen.add(key);
      candidates.push({ title, href });
    };
    const metadata = markup.querySelector('[data-search-candidates]');
    if (metadata) {
      const data = JSON.parse(metadata.textContent);
      const score = title => {
        const name = normalize(title);
        if (name === query) return 3;
        if ((name.match(/[\p{L}\p{N}-]+/gu) || []).some(word => word.startsWith(query))) return 2;
        return name.includes(query) ? 1 : 0;
      };
      data.products.sort((a,b) => score(b.title) - score(a.title));
      const productList = markup.querySelector('[data-search-products-list]');
      data.products.slice(0,4).forEach(product => {
        const item = markup.querySelector(`[data-search-product="${product.id}"]`);
        if (item) productList?.append(item);
      });
      const keep = new Set(data.products.slice(0,4).map(product => String(product.id)));
      markup.querySelectorAll('[data-search-product]').forEach(item => {
        if (!keep.has(item.getAttribute('data-search-product'))) item.remove();
      });
      data.products.slice(0,4).forEach(product => {
        // Offer actual catalog words, never a fabricated list of queries.
        (product.title.match(/[\p{L}\p{N}-]+/gu) || []).forEach(term => {
          if (normalize(term).startsWith(query) && normalize(term) !== query && !stopWords.has(normalize(term))) add(term, `${this.input.form.getAttribute('action')}?${new URLSearchParams({q:term,'options[prefix]':'last'})}`);
        });
      });
      data.collections.forEach(item => add(item.title, item.url));
      metadata.remove();
    }
    const suggestions = markup.querySelector('[data-search-suggestions]');
    const list = suggestions?.querySelector('ul');
    candidates.forEach((item, index) => {
      const li = doc.createElement('li');
      li.id = `predictive-search-option-suggestion-${index + 1}`;
      li.className = 'predictive-search__list-item';
      li.setAttribute('role', 'option'); li.setAttribute('aria-selected', 'false');
      const link = doc.createElement('a');
      link.href = item.href; link.tabIndex = -1; link.className = 'predictive-search__item link link--text';
      const title = doc.createElement('span'); title.className = 'predictive-search__item-heading h5'; title.textContent = item.title;
      link.append(title); li.append(link); list?.append(li);
    });
    if (suggestions) suggestions.hidden = candidates.length === 0;
    const sidebar = markup.querySelector('[data-search-sidebar]');
    if (sidebar) sidebar.hidden = !candidates.length && !sidebar.querySelector('[data-search-pages] li');
    const text = markup.querySelector('[data-predictive-search-search-for-text]');
    if (text) text.textContent = text.getAttribute('data-search-label').replace('%terms%', searchTerm);
    const count = markup.querySelector('[data-predictive-search-live-region-count-value]');
    if (count?.hasAttribute('data-count-label')) {
      count.textContent = count.getAttribute('data-count-label').replace('%count%', markup.querySelectorAll('li[role="option"]').length);
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.requestId++;
      this.abortController.abort();
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
